import type { Router } from 'vue-router'

const VERSION_URL = '/version.json'
/** 定时轮询间隔 */
const POLL_INTERVAL_MS = 60_000
/** 两次版本检查最小间隔，合并 focus / visibility 等重复触发 */
const MIN_CHECK_INTERVAL_MS = 15_000
/** 防止版本异常或 chunk 缺失导致无限刷新 */
const RELOAD_GUARD_KEY = 'kp_app_silent_reload_at'
const RELOAD_COOLDOWN_MS = 30_000

let pollTimer: ReturnType<typeof setInterval> | null = null
let checking = false
let reloading = false
let versionCheckStarted = false
let chunkErrorReloadStarted = false
let lastCheckAt = 0

function canSilentReload(): boolean {
  if (reloading) return false
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0')
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false
    }
  } catch {
    // sessionStorage 不可用时仍允许刷新一次
  }
  return true
}

function markSilentReload(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

/** 静默刷新，不提示用户 */
export function silentReload(): void {
  if (!canSilentReload()) return
  reloading = true
  markSilentReload()
  window.location.reload()
}

function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null
  const name = err?.name || ''
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(err?.message ?? error ?? '')

  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  )
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    // 不用时间戳破坏缓存：配合 nginx no-cache，未变更时可走 304
    const res = await fetch(VERSION_URL, {
      cache: 'no-cache',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null

    const contentType = res.headers.get('content-type') || ''
    // 防止缺文件时 SPA 回退成 index.html 被当成版本
    if (contentType.includes('text/html')) return null

    const data = (await res.json()) as { version?: string }
    return typeof data.version === 'string' && data.version ? data.version : null
  } catch {
    return null
  }
}

async function checkVersionOnce(force = false): Promise<void> {
  if (reloading || checking) return

  const now = Date.now()
  if (!force && now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return

  checking = true
  lastCheckAt = now
  try {
    const localVersion = import.meta.env.VITE_APP_VERSION
    if (!localVersion) return

    const remoteVersion = await fetchRemoteVersion()
    if (!remoteVersion) return

    if (remoteVersion !== localVersion) {
      silentReload()
    }
  } finally {
    checking = false
  }
}

function onPageBecomeActive(): void {
  // 切回标签页时 focus 与 visibilitychange 常一起触发，靠节流合并
  void checkVersionOnce(false)
}

/**
 * 生产环境：轮询 version.json，版本不一致时静默刷新。
 * 开发环境跳过（无构建产物 version.json，且有 HMR）。
 */
export function setupVersionCheck(): void {
  if (!import.meta.env.PROD) return
  if (!import.meta.env.VITE_APP_VERSION) return
  if (versionCheckStarted) return
  versionCheckStarted = true

  // 刚加载完的页面本地版本与远端一致，无需立刻检查；靠轮询 / 回前台检测发版
  if (pollTimer != null) {
    clearInterval(pollTimer)
  }
  pollTimer = setInterval(() => {
    void checkVersionOnce(true)
  }, POLL_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onPageBecomeActive()
    }
  })
  window.addEventListener('focus', onPageBecomeActive)
}

/** 路由懒加载 chunk 失败时静默刷新（发版后旧页面常见） */
export function setupChunkErrorReload(router: Router): void {
  if (chunkErrorReloadStarted) return
  chunkErrorReloadStarted = true

  router.onError((error) => {
    if (isChunkLoadError(error)) {
      silentReload()
    }
  })

  // 部分动态 import 失败不会进 router.onError，需兜底；
  // 与 onError 同时触发时由 reloading / cooldown 去重
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      silentReload()
    }
  })
}
